import React, { useState } from 'react';
import { Table, TableHead, TableBody } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import moment from 'moment';

import { getBranchSmsSenders, deleteSmsSender } from '../../../api/sms';
import { formatLandlinePhone } from '../../../utils/methods';
import SmsSenderRegisterModal from './SmsSenderRegisterModal';
import SmsSenderVerifyGuideModal from './SmsSenderVerifyGuideModal';
import {
  HeaderRow,
  Notice,
  RegisterButton,
  HeaderCell,
  BodyCell,
  DataRow,
  StatusText,
  ActionGroup,
  VerifyButton,
  DeleteButton,
} from './SmsSenderRegisterStyled';

const COLUMNS = [
  { id: 'senderName', label: '발신자명', width: 220 },
  { id: 'senderNumber', label: '발신번호', width: 200 },
  { id: 'regDate', label: '신청일시', width: 180 },
  { id: 'status', label: '인증여부', width: 140 },
  { id: 'action', label: '기타', width: 180 },
];

const STATUS_LABEL = {
  VERIFIED: '인증완료',
  UNDER_REVIEW: '인증요청',
  UNVERIFIED: '미인증',
};

const formatDateTime = value => {
  if (!value) return '-';
  const m = moment(value);
  return m.isValid() ? m.format('YYYY-MM-DD HH:mm') : '-';
};

const formatPhone = number => {
  if (!number) return '-';
  const digits = String(number).replace(/[^0-9]/g, '');
  // 대표번호(1588-XXXX 등) 8자리는 4-4
  if (digits.length === 8) return digits.replace(/(\d{4})(\d{4})/, '$1-$2');
  // 휴대폰/서울(02)/지역 자동 포맷 (공용 헬퍼)
  return formatLandlinePhone(digits) || '-';
};

const SmsSenderRegister = () => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state.user);
  const toast = useToast();
  const queryClient = useQueryClient();
  const branchId = user?.branch?.branchId;

  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);

  const { data: senders = [] } = useQuery({
    queryKey: ['branchSmsSenders', branchId],
    queryFn: () => getBranchSmsSenders(branchId, user?.token),
    enabled: !!branchId && !!user?.token,
  });

  const deleteMutation = useMutation({
    mutationFn: id => deleteSmsSender(branchId, id, user?.token),
    onSuccess: () => {
      toast.success(t('삭제되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['branchSmsSenders', branchId] });
    },
  });

  const handleDelete = sender => {
    if (window.confirm(t('해당 발신번호를 삭제하시겠습니까?'))) {
      deleteMutation.mutate(sender.id);
    }
  };

  const handleRegisterSuccess = () => {
    setIsRegisterModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['branchSmsSenders', branchId] });
  };

  const renderCellValue = (sender, columnId) => {
    switch (columnId) {
      case 'senderName':
        return sender.senderName || '-';
      case 'senderNumber':
        return formatPhone(sender.senderNumber);
      case 'regDate':
        return formatDateTime(sender.regDate);
      case 'status':
        return <StatusText status={sender.status}>{t(STATUS_LABEL[sender.status] || sender.status)}</StatusText>;
      case 'action':
        return (
          <ActionGroup>
            {sender.status !== 'VERIFIED' && (
              <VerifyButton onClick={() => setIsGuideModalOpen(true)}>{t('인증하기')}</VerifyButton>
            )}
            <DeleteButton onClick={() => handleDelete(sender)} disabled={deleteMutation.isPending}>
              {t('삭제')}
            </DeleteButton>
          </ActionGroup>
        );
      default:
        return '-';
    }
  };

  return (
    <>
      <HeaderRow>
        <Notice>{t('발신번호를 등록하면 관리자 승인 후 문자 발송에 사용할 수 있습니다.')}</Notice>
        <RegisterButton onClick={() => setIsRegisterModalOpen(true)}>{t('발신번호 등록')}</RegisterButton>
      </HeaderRow>

      <Table>
        <TableHead>
          <DataRow>
            {COLUMNS.map(col => (
              <HeaderCell key={col.id} style={{ width: col.width || 'auto' }}>
                {t(col.label)}
              </HeaderCell>
            ))}
          </DataRow>
        </TableHead>
        <TableBody>
          {senders.length > 0 ? (
            senders.map((sender, idx) => (
              <DataRow key={sender.id || idx}>
                {COLUMNS.map(col => (
                  <BodyCell key={col.id} style={{ width: col.width || 'auto' }}>
                    {renderCellValue(sender, col.id)}
                  </BodyCell>
                ))}
              </DataRow>
            ))
          ) : (
            <DataRow>
              <BodyCell colSpan={COLUMNS.length} style={{ textAlign: 'center', color: '#a6a6a6' }}>
                {t('등록된 발신번호가 없습니다')}
              </BodyCell>
            </DataRow>
          )}
        </TableBody>
      </Table>

      {isRegisterModalOpen && (
        <SmsSenderRegisterModal
          open={isRegisterModalOpen}
          branchId={branchId}
          onClose={() => setIsRegisterModalOpen(false)}
          onSuccess={handleRegisterSuccess}
        />
      )}

      {isGuideModalOpen && (
        <SmsSenderVerifyGuideModal open={isGuideModalOpen} onClose={() => setIsGuideModalOpen(false)} />
      )}
    </>
  );
};

export default SmsSenderRegister;
